import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit
} from '@angular/core';
import { FormArray } from '@angular/forms';
import {
  NgbActiveModal,
  NgbDateAdapter,
  NgbDateNativeAdapter,
  NgbDateParserFormatter,
  NgbModal
} from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, from, of } from 'rxjs';
import { LicenseForm } from 'src/app/classes/form-groups/license-form';
import { PrimaryTradeForm } from 'src/app/classes/form-groups/primary-trade-form';
import { PrimaryTradeLicenseRequirementForm } from 'src/app/classes/form-groups/primary-trade-license-requirement-form';
import { SubNationalDivisionForm } from 'src/app/classes/form-groups/sub-national-division-form';
import { VendorTradeForm } from 'src/app/classes/form-groups/vendor-trade-form';
import {
  License,
  PrimaryTrade,
  PrimaryTradeLicenseRequirement,
  SubNationalDivision,
  VendorTrade
} from 'src/app/classes/models';
import { CustomNgbDateFormatter } from 'src/app/dependencies/custom-ngb-date-formatter';
import { LicenseService } from 'src/app/services/license.service';
import { SubNationalDivisionService } from 'src/app/services/sub-national-division.service';
import { ToastService } from 'src/app/services/toast.service';
import { TradeService } from 'src/app/services/trade.service';
import { UtilityService } from 'src/app/services/utility.service';

import { LicenseUploadSelectionModalComponent } from '../license-upload-selection-modal/license-upload-selection-modal.component';

@Component({
  selector: 'app-edit-license-modal',
  templateUrl: './edit-license-modal.component.html',
  styleUrls: ['./edit-license-modal.component.less'],
  providers: [
    { provide: NgbDateAdapter, useClass: NgbDateNativeAdapter },
    { provide: NgbDateParserFormatter, useClass: CustomNgbDateFormatter }
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class EditLicenseModalComponent implements OnInit {
  constructor(
    private ToastService: ToastService,
    private ActiveModal: NgbActiveModal,
    private TradeService: TradeService,
    private SubNationalDivisionService: SubNationalDivisionService,
    private UtilityService: UtilityService,
    private LicenseService: LicenseService,
    private ModalService: NgbModal
  ) {}

  LicenseFormGroup = new LicenseForm();
  PrimaryTradeLicenseRequirementForm = new PrimaryTradeLicenseRequirementForm();

  @Input() VendorId!: number;
  @Input() country!: 'US' | 'CA';
  @Input() License?: License;
  @Input() VendorTrade?: VendorTrade;
  @Input() PrimaryTradeLicenseRequirement!: PrimaryTradeLicenseRequirement;

  VerificationTypes: [
    { label: ''; value: null },
    { label: 'Online Verified'; value: 'Online Verified' },
    { label: 'Image Verified'; value: 'Image Verified' }
  ] = [
    { label: '', value: null },
    { label: 'Online Verified', value: 'Online Verified' },
    { label: 'Image Verified', value: 'Image Verified' }
  ];

  LicensorRegions: [
    { label: ''; value: null },
    { label: 'State'; value: 'State' },
    { label: 'Municipality'; value: 'Municipality' },
    { label: 'Both'; value: 'Both' },
    { label: 'Province'; value: 'Province' }
  ] = [
    { label: '', value: null },
    { label: 'State', value: 'State' },
    { label: 'Municipality', value: 'Municipality' },
    { label: 'Both', value: 'Both' },
    { label: 'Province', value: 'Province' }
  ];

  Statuses: [
    { label: ''; value: null },
    { label: 'Active'; value: 'Active' },
    { label: 'Revoked'; value: 'Revoked' },
    { label: 'Lapsed'; value: 'Lapsed' },
    { label: 'Archived'; value: 'Archived' }
  ] = [
    { label: '', value: null },
    { label: 'Active', value: 'Active' },
    { label: 'Revoked', value: 'Revoked' },
    { label: 'Lapsed', value: 'Lapsed' },
    { label: 'Archived', value: 'Archived' }
  ];

  Trades: PrimaryTrade[] = [];
  SubNationalDivisions: SubNationalDivision[] = [];

  goToTradeLookupListing() {
    window.open(
      'https://getvived.sharepoint.com/:x:/g/EQ3Sh5LPXPdEq2zdrOdOWeIBXnXUemUIMFzFGWS8UKL6zQ?e=ehG5D8',
      '_blank'
    );
  }

  viewUploadedLicense() {
    if (!this.License) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing license information.',
        { returnError: true }
      );
    }

    const { id } = this.License;

    if (!id) {
      throw this.ToastService.addErrorToast('Error', 'License is missing Id.', {
        returnError: true
      });
    }

    this.LicenseService.getLicense(
      this.UtilityService.stringifyObjectsInQueryObject({
        attributes: JSON.stringify(['id', 'licenseFile']),
        status: {
          $or: [{ $eq: 'Archived' }, { $ne: 'Archived' }, { $eq: null }]
        },
        id,
        LicenseFile: {}
      })
    ).subscribe({
      next: (License) => {
        const { licenseFile, LicenseFile } = License;

        if (LicenseFile) {
          this.LicenseService.getLicenseFileS3PresignedUrl(
            this.VendorId,
            id
          ).subscribe({
            next: (s3Response) => {
              const { presignedUrl, filename } = s3Response;
              window.open(presignedUrl, '_blank');
            }
          });
        } else {
          if (!licenseFile) {
            throw this.ToastService.addErrorToast(
              'Error',
              'License is missing File.',
              { returnError: true }
            );
          }

          const licenseFileParts = licenseFile.split(';base64,');
          const fileType = licenseFileParts[0].replace('data:', '');
          const ArrayBuffer =
            this.UtilityService.convertBase64StringToArrayBuffer(
              licenseFileParts[1]
            );
          window.open(
            URL.createObjectURL(new Blob([ArrayBuffer], { type: fileType })),
            '_blank'
          );
        }
      }
    });
  }

  uploadLicense() {
    const {
      value: { id: PrimaryTradeLicenseRequirementId, PrimaryTrade, Licenses }
    } = this.PrimaryTradeLicenseRequirementForm;

    if (!PrimaryTrade) {
      throw this.ToastService.addErrorToast('Error', 'Missing Primary Trade.', {
        returnError: true
      });
    }

    if (!PrimaryTradeLicenseRequirementId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'License requirement is missing Id.',
        { returnError: true }
      );
    }

    const { componentInstance, result } = this.ModalService.open(
      LicenseUploadSelectionModalComponent,
      { backdrop: 'static', modalDialogClass: 'license-upload-selection-modal' }
    );

    const LicenseControls =
      this.PrimaryTradeLicenseRequirementForm.controls['Licenses'];

    if (!LicenseControls) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing license controls.',
        { returnError: true }
      );
    }

    const CurrentLicenseControl = LicenseControls.at(0);
    const {
      value: { id: LicenseId }
    } = CurrentLicenseControl;

    // If the license form group is passed through without an id (no license), remove it to attach
    // new license.
    if (!LicenseId) {
      LicenseControls.clear();
    }

    const PrimaryTradeLicenseRequirementForms: PrimaryTradeLicenseRequirementForm[] =
      [this.PrimaryTradeLicenseRequirementForm];

    Object.assign(componentInstance, {
      VendorId: this.VendorId,
      VendorTrade: this.VendorTrade,
      PrimaryTradeLicenseRequirement: this.PrimaryTradeLicenseRequirement!,
      PrimaryTradeLicenseRequirementForms
    });

    from(result).subscribe({
      next: () => {
        const LicenseControls =
          this.PrimaryTradeLicenseRequirementForm.controls['Licenses'];

        if (!LicenseControls) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing license controls.',
            { returnError: true }
          );
        }

        const CurrentLicenseControl = LicenseControls.at(0);

        const { value: License } = CurrentLicenseControl;

        if (License) {
          this.LicenseFormGroup.patchValue(License);
          this.syncLicenseFileValidation(License);
        }
      }
    });
  }

  private syncLicenseFileValidation(
    license?: Pick<License, 'LicenseFileId' | 'licenseFile'> | null
  ): void {
    const hasExistingFile = Boolean(
      license?.LicenseFileId || license?.licenseFile
    );
    this.LicenseFormGroup.applyLicenseFileValidationForExistingFile(
      hasExistingFile
    );
  }

  close() {
    this.ActiveModal.dismiss();
  }

  save() {
    const errors = this.UtilityService.generateValidationErrorsObject(
      this.LicenseFormGroup
    );

    if (errors) {
      const { licenseFile } = errors;
      const { LicenseFileId } = this.LicenseFormGroup.value;

      if (licenseFile && !LicenseFileId) {
        throw this.ToastService.addErrorToast(
          'Error',
          'License file has not been uploaded.',
          { returnError: true }
        );
      }

      throw this.ToastService.addErrorToast(
        'Error',
        'Missing required information.',
        { returnError: true }
      );
    }

    const License = this.LicenseFormGroup.value;

    this.LicenseService.saveLicense(this.VendorId, License).subscribe({
      next: (
        savedLicense: Pick<
          License,
          'status' | 'licenseNumber' | 'expirationDate' | 'licenseState'
        >
      ) => {
        Object.assign(this.VendorTrade!, { License: savedLicense });
        this.ToastService.addSuccessToast(
          'Success',
          'License saved successfully.'
        );
        this.ActiveModal.close(License);
      }
    });
  }

  ngOnInit(): void {
    if (!this.VendorId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
        returnError: true
      });
    }

    if (!this.country) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing country information.',
        { returnError: true }
      );
    }

    if (!this.VendorTrade) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing vendor trade information.',
        { returnError: true }
      );
    }

    if (!this.PrimaryTradeLicenseRequirement) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing trade license requirement information.',
        { returnError: true }
      );
    }

    this.PrimaryTradeLicenseRequirementForm.setControl(
      'PrimaryTrade',
      new PrimaryTradeForm()
    );
    this.PrimaryTradeLicenseRequirementForm.setControl(
      'SubNationalDivision',
      new SubNationalDivisionForm()
    );
    this.PrimaryTradeLicenseRequirementForm.setControl(
      'Licenses',
      new FormArray([])
    );
    this.PrimaryTradeLicenseRequirementForm.setControl(
      'VendorTrade',
      new VendorTradeForm()
    );

    this.PrimaryTradeLicenseRequirementForm.patchValue(
      this.PrimaryTradeLicenseRequirement
    );

    this.LicenseFormGroup.patchValue({
      VendorId: this.VendorId,
      state: this.PrimaryTradeLicenseRequirement.state
    });

    forkJoin([
      (() => {
        if (this.License && this.License.id) {
          return this.LicenseService.getLicense(
            this.UtilityService.stringifyObjectsInQueryObject({
              id: this.License.id,
              status: {
                $or: [{ $eq: 'Archived' }, { $ne: 'Archived' }, { $eq: null }]
              },
              TradeLicense: {
                VendorTrade: {},
                PrimaryTradeLicenseRequirement: {}
              }
            })
          );
        }

        return of<License | null>(null);
      })(),
      this.TradeService.getTrades(
        this.UtilityService.stringifyObjectsInQueryObject({
          order: [['name', 'ASC']]
        })
      ),
      this.SubNationalDivisionService.getSubNationalDivisions({
        country: this.country
      })
    ]).subscribe({
      next: ([License, Trades, SubNationalDivisions]) => {
        this.Trades.push(...Trades);

        this.SubNationalDivisions.push(...SubNationalDivisions);

        if (License) {
          this.LicenseFormGroup.patchValue(License);
          this.syncLicenseFileValidation(License);
        }

        const { PrimaryTradeId } = this.VendorTrade!;

        if (PrimaryTradeId) {
          this.LicenseFormGroup.patchValue({ PrimaryTradeId });
        }

        this.PrimaryTradeLicenseRequirementForm.controls['Licenses']!.push(
          this.LicenseFormGroup
        );
      }
    });
  }
}
